import clsx from 'clsx';
import type { ComponentPropsWithRef, ElementType, FunctionComponent, ReactElement, ReactNode } from 'react';

import { Button } from '../../core/button/Button';
import { IconChevronDownSmall } from '../../core/icons';
import type { SideNavigationShortcut } from './SideNavigationTooltip';
import { SideNavigationTooltip } from './SideNavigationTooltip';
import { navRowClassName, navRowIconClassName, navRowLabelClassName } from './side-navigation-classes';

type GroupToggle = {
    readonly label: string;
    readonly onToggle: () => void;
};

const chevronClassName = (folded: boolean, visible: boolean): string =>
    clsx(
        'size-4 shrink-0 text-text-content-faint transition-[rotate,opacity] duration-150 motion-reduce:transition-none',
        folded && '-rotate-90',
        visible ? 'opacity-100' : 'opacity-0',
    );

type SideNavigationGroupOwnProps<E extends ElementType> = {
    readonly active?: boolean;
    readonly as?: E;
    readonly children: ReactNode;
    readonly current?: boolean;
    readonly expanded: boolean;
    readonly folded: boolean;
    // Set aside whole, as when the rail has no room for it.
    readonly hidden?: boolean;
    readonly Icon: FunctionComponent<{ readonly className?: string }>;
    readonly IconAdornment?: FunctionComponent;
    readonly label: string;
    readonly onHeaderClick: () => void;
    readonly shortcut?: SideNavigationShortcut;
    readonly toggle?: GroupToggle;
};

export type SideNavigationGroupProps<E extends ElementType = 'button'> = SideNavigationGroupOwnProps<E> &
    Omit<ComponentPropsWithRef<E>, keyof SideNavigationGroupOwnProps<E> | 'className'>;

// A parent row with its children listed under it. Without a toggle the header
// folds the group on the open rail; on the folded rail the caller decides what
// a click does, usually opening the first child. With a toggle the header opens
// the group's own page, rendered as the app's router link through `as`, and the
// chevron folds the group on its own. An open group stands apart from its
// neighbours, with room above it and below its last child, and its children
// slide open and shut; folded away, they stay mounted but inert.
export const SideNavigationGroup = <E extends ElementType = 'button'>({
    active = false,
    as,
    children,
    current = false,
    expanded,
    folded,
    hidden = false,
    Icon,
    IconAdornment,
    label,
    onHeaderClick,
    shortcut,
    toggle,
    ...headerProps
}: SideNavigationGroupProps<E>): ReactElement => {
    const Component = as ?? 'button';
    const header = (
        <Component
            aria-current={current ? 'page' : undefined}
            aria-expanded={toggle === undefined ? !folded : undefined}
            aria-label={label}
            className={clsx(
                navRowClassName(active),
                toggle !== undefined &&
                    !active &&
                    'group-hover/nav-group:bg-surface-interactive group-hover/nav-group:text-text-content-strong',
            )}
            onClick={onHeaderClick}
            {...(as === undefined ? { type: 'button' } : {})}
            {...headerProps}
        >
            <span aria-hidden={true} className={navRowIconClassName(active)}>
                <Icon className="size-[18px]" />
                {IconAdornment ? <IconAdornment /> : null}
            </span>
            <span className={navRowLabelClassName(expanded)}>{label}</span>
            {toggle === undefined ? (
                <IconChevronDownSmall aria-hidden={true} className={chevronClassName(folded, expanded)} />
            ) : (
                <span aria-hidden={true} className="size-4 shrink-0" />
            )}
        </Component>
    );

    return (
        <li
            hidden={hidden}
            className={clsx(
                // The hidden attribute wins over the flex display, so the rail can set the group aside and measure it back.
                'flex flex-col transition-[margin] duration-standard ease-enter motion-reduce:transition-none [&[hidden]]:hidden',
                !folded && 'mb-xxl not-first:mt-xxl',
            )}
        >
            <div className="group/nav-group relative">
                <SideNavigationTooltip expanded={expanded} label={label} shortcut={shortcut}>
                    {header}
                </SideNavigationTooltip>
                {toggle !== undefined && expanded ? (
                    <Button
                        aria-expanded={!folded}
                        aria-label={toggle.label}
                        className="absolute top-1/2 right-xs flex size-6 -translate-y-1/2 items-center justify-center rounded-sm border-0 bg-transparent p-0 hover:bg-surface-interactive"
                        variant="plain"
                        onClick={toggle.onToggle}
                    >
                        <IconChevronDownSmall aria-hidden={true} className={chevronClassName(folded, true)} />
                    </Button>
                ) : null}
            </div>
            <div
                aria-hidden={folded}
                className={clsx(
                    'grid transition-[grid-template-rows] duration-standard ease-enter motion-reduce:transition-none',
                    folded ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
                )}
                inert={folded}
            >
                <div
                    className={clsx(
                        'min-h-0 overflow-hidden transition-opacity duration-standard ease-enter motion-reduce:transition-none',
                        folded ? 'opacity-0' : 'opacity-100',
                    )}
                >
                    <ul
                        aria-label={label}
                        className={clsx(
                            'm-0 flex list-none flex-col p-0 pt-xxs',
                            // Folded, the stripes' rows touch, so the pointer never falls between two.
                            expanded ? 'gap-xxs' : 'gap-0',
                        )}
                        data-side-nav-children=""
                    >
                        {children}
                    </ul>
                </div>
            </div>
        </li>
    );
};
